import { useState } from 'react';
import Modal from '../../components/Modal';
import DynamicFields from '../../components/DynamicFields';
import { CrmApi } from '../../api/resources';
import { errorMessage, validationErrors } from '../../api/client';
import { fromLocalInput, toLocalInput } from './useCrmLookups';

/**
 * Lead registration (no `lead`) or edit (with `lead`).
 * Fixed fields first, then admin-defined custom fields.
 */
export default function LeadFormModal({ lead, lookups, onClose, onSaved }) {
  const editing = !!lead;
  const defaultStatus = lookups.statuses.find((s) => s.is_default) || lookups.statuses[0];

  const [form, setForm] = useState({
    name: lead?.name || '',
    phone: lead?.phone || '',
    email: lead?.email || '',
    company: lead?.company || '',
    source: lead?.source || '',
    notes: lead?.notes || '',
    status_id: lead?.status_id || defaultStatus?.id || '',
    assigned_to: lead?.assigned_to || '',
    next_follow_up_at: toLocalInput(lead?.next_follow_up_at),
    custom_values: { ...(lead?.custom_values || {}) },
  });
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    setBanner('');
    const payload = {
      ...form,
      status_id: form.status_id || null,
      assigned_to: form.assigned_to || null,
      next_follow_up_at: fromLocalInput(form.next_follow_up_at),
    };
    try {
      if (editing) await CrmApi.leads.update(lead.id, payload);
      else await CrmApi.leads.create(payload);
      onSaved();
    } catch (err) {
      setErrors(validationErrors(err));
      setBanner(errorMessage(err, 'Could not save lead.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={editing ? `Edit ${lead.name}` : 'Register lead'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {banner && <div className="notice notice-error">{banner}</div>}
      <form onSubmit={submit}>
        <div className="field">
          <label>Name *</label>
          <input type="text" value={form.name} onChange={set('name')} />
          {errors.name && <div className="err">{errors.name}</div>}
        </div>
        <div className="field">
          <label>Phone *</label>
          <input type="text" value={form.phone} onChange={set('phone')} />
          {errors.phone && <div className="err">{errors.phone}</div>}
        </div>
        <div className="field">
          <label>Email</label>
          <input type="email" value={form.email} onChange={set('email')} />
          {errors.email && <div className="err">{errors.email}</div>}
        </div>
        <div className="field">
          <label>Company</label>
          <input type="text" value={form.company} onChange={set('company')} />
        </div>
        <div className="field">
          <label>Source</label>
          <input
            type="text"
            placeholder="Website, referral, walk-in…"
            value={form.source}
            onChange={set('source')}
          />
        </div>
        {!editing && (
          <div className="field">
            <label>Status</label>
            <select value={form.status_id} onChange={set('status_id')}>
              {lookups.statuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="field">
          <label>Assigned to</label>
          <select value={form.assigned_to} onChange={set('assigned_to')}>
            <option value="">Unassigned</option>
            {lookups.users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Next follow-up</label>
          <input
            type="datetime-local"
            value={form.next_follow_up_at}
            onChange={set('next_follow_up_at')}
          />
        </div>
        <div className="field">
          <label>Notes</label>
          <textarea rows={3} value={form.notes} onChange={set('notes')} />
        </div>

        <DynamicFields
          fields={lookups.leadFields}
          values={form.custom_values}
          errors={errors}
          onChange={(custom_values) => setForm((f) => ({ ...f, custom_values }))}
        />
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
